import { useMemo, useState } from "react";
import {
  ArrowLeft, Search, Filter, LayoutGrid, Calendar, Edit3, Share2,
  RefreshCw, MessageCircle, ArrowUpRight, CheckCircle2, AlertTriangle,
  Stethoscope, Building2, GraduationCap, Phone, Mail, MapPin, Hash,
  BadgeCheck, Scissors, Settings2, X, Check, Plus, Trash2, UserPlus,
  Pill, Syringe, FileText, ChevronRight, ChevronLeft, LayoutDashboard,
  Briefcase, type LucideIcon,
} from "lucide-react";

type TabKey = "vue" | "identite" | "specialites" | "cabinet" | "ngap" | "pharmacie" | "personnel" | "projets";
const TABS: { k: TabKey; label: string; icon: LucideIcon }[] = [
  { k: "vue", label: "Vue d'ensemble", icon: LayoutDashboard },
  { k: "identite", label: "Identité", icon: BadgeCheck },
  { k: "specialites", label: "Spécialités", icon: Stethoscope },
  { k: "cabinet", label: "Cabinet", icon: Building2 },
  { k: "ngap", label: "NGAP", icon: FileText },
  { k: "pharmacie", label: "Pharmacie", icon: Pill },
  { k: "personnel", label: "Personnel", icon: UserPlus },
  { k: "projets", label: "Projets", icon: Briefcase },
];

type NgapAct = { code: string; label: string; base: number; coef: number };
type Staff = { id: string; name: string; role: "Infirmier" | "Aide-soignant" | "Secrétaire"; phone: string; access: "Lecture" | "Lecture/Écriture" | "Admin" };

type Doctor = {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar: string;
  rate: string;
  experience: string;
  hoursWorked: { warn: number; ok: number };
  exp: { warn: number; ok: number };
  projects: { warn: number; ok: number };
  status: "active" | "overload" | "ok";
  dots: { color: "warn" | "muted" | "ok"; n: number }[];
  inpe: string;
  authNumber: string;
  joinDate: string;
  speciality: string;
  subSpecialities: string[];
  surgical: boolean;
  clinic: string;
  city: string;
  address: string;
  phone: string;
  email: string;
  faculty: string;
  ngap: NgapAct[];
  pharmacyRestrictions: string[];
  medicalDevices: string[];
  staff: Staff[];
};

const ALL_SPECIALITIES = [
  "Anesthésie-Réanimation","Cardiologie","Dermatologie","Endocrinologie",
  "Gastro-entérologie","Gynécologie","Hématologie clinique","Médecine interne",
  "Médecine nucléaire","Médecine physique","Maladies infectieuses",
  "Médecine Légale","Néphrologie","Pédiatrie",
];

const DEFAULT_NGAP: NgapAct[] = [
  { code: "C", label: "Consultation", base: 25, coef: 1 },
  { code: "CS", label: "Consultation spécialiste", base: 50, coef: 1 },
];

const initialDoctors: Doctor[] = [
  {
    id: "1", firstName: "Sam", lastName: "Frank", role: "Cardiologue",
    avatar: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop",
    rate: "650 MAD/h", experience: "76m",
    hoursWorked: { warn: 18, ok: 112 }, exp: { warn: 1, ok: 4 }, projects: { warn: 0, ok: 2 },
    status: "ok", dots: [{ color: "warn", n: 1 }, { color: "muted", n: 3 }],
    inpe: "10293847", authNumber: "10293/08", joinDate: "03/22/2021",
    speciality: "Cardiologie", subSpecialities: ["Cardiologie"], surgical: false,
    clinic: "Clinique Atlas", city: "Casablanca",
    address: "12 rue des Orangers, Casablanca", phone: "+212 5 22 11 22 33",
    email: "s.frank@dikdoc.ma", faculty: "Faculté de Médecine de Casablanca",
    ngap: DEFAULT_NGAP, pharmacyRestrictions: [], medicalDevices: [], staff: [],
  },
  {
    id: "2", firstName: "Nikki", lastName: "Qlay", role: "Dermatologue",
    avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=200&h=200&fit=crop",
    rate: "750 MAD/h", experience: "92m",
    hoursWorked: { warn: 24, ok: 130 }, exp: { warn: 2, ok: 5 }, projects: { warn: 1, ok: 3 },
    status: "overload", dots: [{ color: "warn", n: 2 }, { color: "ok", n: 3 }],
    inpe: "11223344", authNumber: "11223/15", joinDate: "06/14/2019",
    speciality: "Dermatologie", subSpecialities: ["Dermatologie"], surgical: true,
    clinic: "Polyclinique Anfa", city: "Casablanca",
    address: "Bd. Anfa, Casablanca", phone: "+212 5 22 99 88 77",
    email: "n.qlay@dikdoc.ma", faculty: "Faculté de Médecine de Rabat",
    ngap: DEFAULT_NGAP, pharmacyRestrictions: [], medicalDevices: [], staff: [],
  },
  {
    id: "3", firstName: "Emily", lastName: "Smith", role: "Pédiatre",
    avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop",
    rate: "800 MAD/h", experience: "104m",
    hoursWorked: { warn: 30, ok: 140 }, exp: { warn: 1, ok: 4 }, projects: { warn: 1, ok: 2 },
    status: "overload", dots: [{ color: "warn", n: 2 }, { color: "ok", n: 3 }],
    inpe: "20394857", authNumber: "20394/11", joinDate: "11/02/2018",
    speciality: "Pédiatrie", subSpecialities: ["Pédiatrie"], surgical: false,
    clinic: "Clinique Badr", city: "Rabat",
    address: "Av. Mohammed V, Rabat", phone: "+212 5 37 33 22 11",
    email: "e.smith@dikdoc.ma", faculty: "Faculté de Médecine de Rabat",
    ngap: DEFAULT_NGAP, pharmacyRestrictions: [], medicalDevices: [], staff: [],
  },
  {
    id: "4", firstName: "Dan", lastName: "Johnson", role: "Médecin interne",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop",
    rate: "600 MAD/h", experience: "60m",
    hoursWorked: { warn: 12, ok: 96 }, exp: { warn: 0, ok: 3 }, projects: { warn: 0, ok: 4 },
    status: "ok", dots: [{ color: "ok", n: 5 }],
    inpe: "30495867", authNumber: "30495/19", joinDate: "08/30/2022",
    speciality: "Médecine interne", subSpecialities: ["Médecine interne"], surgical: false,
    clinic: "Hôpital Cheikh Zaid", city: "Rabat",
    address: "Hay Riad, Rabat", phone: "+212 5 37 68 68 68",
    email: "d.johnson@dikdoc.ma", faculty: "Faculté de Médecine de Fès",
    ngap: DEFAULT_NGAP, pharmacyRestrictions: [], medicalDevices: [], staff: [],
  },
  {
    id: "5", firstName: "Sarah", lastName: "Davis", role: "Gynécologue",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop",
    rate: "720 MAD/h", experience: "80m",
    hoursWorked: { warn: 20, ok: 120 }, exp: { warn: 1, ok: 3 }, projects: { warn: 1, ok: 2 },
    status: "overload", dots: [{ color: "warn", n: 2 }, { color: "ok", n: 3 }],
    inpe: "40586977", authNumber: "40586/14", joinDate: "02/18/2020",
    speciality: "Gynécologie", subSpecialities: ["Gynécologie"], surgical: true,
    clinic: "Clinique Yasmine", city: "Marrakech",
    address: "Gueliz, Marrakech", phone: "+212 5 24 44 33 22",
    email: "s.davis@dikdoc.ma", faculty: "Faculté de Médecine de Marrakech",
    ngap: DEFAULT_NGAP, pharmacyRestrictions: [], medicalDevices: [], staff: [],
  },
  {
    id: "william", firstName: "William", lastName: "Tennessee", role: "Néphrologue",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
    rate: "780 MAD/h", experience: "84m",
    hoursWorked: { warn: 24, ok: 136 }, exp: { warn: 2, ok: 5 }, projects: { warn: 1, ok: 3 },
    status: "overload", dots: [{ color: "warn", n: 2 }, { color: "ok", n: 3 }],
    inpe: "12345678", authNumber: "12345/12", joinDate: "01/15/2023",
    speciality: "Néphrologie",
    subSpecialities: ["Néphrologie", "Médecine interne"], surgical: true,
    clinic: "Clinique Essaâda", city: "Safi",
    address: "Clinique Essaâda, Av. Hassan II, Safi", phone: "+212 5 24 00 00 00",
    email: "contact@dikdoc.ma", faculty: "Faculté de Médecine et de Pharmacie Marrakech",
    ngap: [
      { code: "C", label: "Consultation", base: 25, coef: 1 },
      { code: "CS", label: "Consultation spécialiste", base: 50, coef: 1 },
      { code: "K5", label: "Hémodialyse — séance", base: 15, coef: 5 },
    ],
    pharmacyRestrictions: ["Stupéfiants — sur ordonnance sécurisée"],
    medicalDevices: ["Générateur d'hémodialyse"],
    staff: [
      { id: "s1", name: "Amal Benani", role: "Infirmier", phone: "+212 6 11 22 33 44", access: "Lecture/Écriture" },
    ],
  },
];

const PROJECTS = [
  { name: "Cardio Ward", icon: "◐", hours: "142h", period: "2024.05 – 2026.06", warn: true },
  { name: "Téléconsult", icon: "◡", hours: "120h", period: "2024.07 – 2026.06", warn: false },
  { name: "Hémodialyse", icon: "HD", hours: "120h", period: "2024.07 – 2026.06", warn: false },
];

export default function DoctorsView({ onBack }: { onBack: () => void }) {
  const [doctors, setDoctors] = useState<Doctor[]>(initialDoctors);
  const [selectedId, setSelectedId] = useState<string>("william");
  const [query, setQuery] = useState("");
  const [specFilter, setSpecFilter] = useState<string>("Toutes");
  const [surgeryFilter, setSurgeryFilter] = useState<"all" | "yes" | "no">("all");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>("vue");

  const filtered = useMemo(() => {
    return doctors.filter((d) => {
      const matchName = `${d.firstName} ${d.lastName}`.toLowerCase().includes(query.toLowerCase());
      const matchSpec = specFilter === "Toutes" || d.subSpecialities.includes(specFilter) || d.speciality === specFilter;
      const matchSurg = surgeryFilter === "all" || (surgeryFilter === "yes" ? d.surgical : !d.surgical);
      return matchName && matchSpec && matchSurg;
    });
  }, [doctors, query, specFilter, surgeryFilter]);

  const selected = doctors.find((d) => d.id === selectedId) ?? doctors[0];
  const updateDoctor = (next: Doctor) =>
    setDoctors((prev) => prev.map((d) => (d.id === next.id ? next : d)));

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onBack} className="icon-btn"><ArrowLeft size={15} /></button>
          <div className="min-w-0">
            <h2 className="text-xl font-semibold tracking-tight">Médecins</h2>
            <div className="truncate text-xs text-muted-foreground">
              Identité · Spécialités · NGAP · Pharmacie · Personnel paramédical
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWizardOpen(true)}
            className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-medium hover:opacity-90"
          >
            <Settings2 size={14} /> Configurer le médecin
          </button>
          {[RefreshCw, Filter, Calendar, Edit3, Share2, LayoutGrid].map((Ic, i) => (
            <button key={i} className="icon-btn"><Ic size={15} /></button>
          ))}
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex items-center gap-3 overflow-x-auto pb-1">
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            aria-label="Rechercher"
            className="group grid h-9 w-9 place-items-center rounded-full border border-border bg-surface-2 transition hover:border-foreground/40 hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 active:scale-95"
          >
            <Search size={14} className="transition group-hover:text-foreground" />
          </button>
          <button
            type="button"
            aria-label="Filtres"
            className="group grid h-9 w-9 place-items-center rounded-full border border-border bg-surface-2 transition hover:border-foreground/40 hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 active:scale-95"
          >
            <Filter size={14} className="transition group-hover:text-foreground" />
          </button>
        </div>
        <div role="tablist" aria-label="Sections médecin" className="flex items-center gap-2 flex-nowrap">
          {TABS.map(({ k, label, icon: Ic }) => {
            const active = tab === k;
            return (
              <button
                key={k}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(k)}
                className={[
                  "group shrink-0 inline-flex items-center gap-2 rounded-full h-9 pl-3 pr-4 text-sm font-medium whitespace-nowrap",
                  "border transition-all duration-200 ease-out",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  "active:scale-[0.97]",
                  active
                    ? "bg-foreground text-background border-foreground shadow-[0_6px_20px_-8px_rgba(0,0,0,0.45)]"
                    : "bg-surface-2 text-foreground/75 border-border hover:text-foreground hover:bg-surface-3 hover:border-foreground/30",
                ].join(" ")}
              >
                <Ic
                  size={14}
                  className={active ? "" : "text-muted-foreground group-hover:text-foreground transition"}
                />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-12 gap-5">
        {/* Personnel list */}
        <aside className="col-span-12 md:col-span-4 lg:col-span-3 card-surface rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Personnel médical</h3>
            <span className="text-[11px] text-muted-foreground">{filtered.length} / {doctors.length}</span>
          </div>

          <div className="mt-3 flex items-center gap-2 rounded-xl border border-border bg-surface-2 px-3 py-2">
            <Search size={13} className="text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher un médecin…"
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground">
                <X size={13} />
              </button>
            )}
          </div>

          <ul className="mt-4 space-y-2">
            {filtered.length === 0 && (
              <li className="rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                Aucun médecin ne correspond.
              </li>
            )}
            {filtered.map((d) => {
              const active = d.id === selectedId;
              return (
                <li key={d.id}>
                  <button
                    onClick={() => setSelectedId(d.id)}
                    className={`flex w-full items-center gap-3 rounded-2xl border p-2.5 text-left transition ${
                      active ? "border-accent/60 bg-surface-2" : "border-transparent hover:bg-surface-2/60"
                    }`}
                  >
                    {/* React sanitizes img src; no `javascript:` allowed. Snyk CWE-79 false positive on dynamic src. */}
                    <img src={d.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{d.firstName} {d.lastName}</p>
                      <p className="truncate text-xs text-muted-foreground">{d.speciality}{d.surgical ? " · Chir." : ""}</p>
                    </div>
                    <span className={`grid h-7 w-7 place-items-center rounded-full text-[11px] ${
                      d.status === "overload"
                        ? "bg-danger/15 text-danger ring-1 ring-danger/40"
                        : "bg-accent/15 text-accent ring-1 ring-accent/40"
                    }`}>
                      {d.status === "overload" ? <AlertTriangle size={13} /> : <CheckCircle2 size={13} />}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Detail */}
        <section key={`${selected.id}-${tab}`} className="col-span-12 md:col-span-8 lg:col-span-9 animate-in fade-in slide-in-from-bottom-1 duration-300">
          <DoctorDetail doctor={selected} tab={tab} onConfigure={() => setWizardOpen(true)} />
        </section>
      </div>

      {wizardOpen && (
        <DoctorWizard
          doctor={selected}
          onClose={() => setWizardOpen(false)}
          onSave={(d) => { updateDoctor(d); setWizardOpen(false); }}
        />
      )}
    </div>
  );
}

function DoctorDetail({ doctor, tab, onConfigure }: { doctor: Doctor; tab: TabKey; onConfigure: () => void }) {
  const show = (k: TabKey) => tab === "vue" || tab === k;
  return (
    <div className="grid grid-cols-12 gap-5">
      <div className="col-span-12 lg:col-span-5 card-surface rounded-2xl overflow-hidden">
        <div className="relative">
          <img src={doctor.avatar} alt={doctor.firstName} className="h-72 w-full object-cover" />
          <div className="absolute inset-x-4 top-4 flex justify-end">
            <button className="icon-btn bg-background/40 backdrop-blur"><ArrowUpRight size={15} /></button>
          </div>
          <div className="absolute right-5 bottom-5 text-right">
            <h3 className="text-3xl font-bold leading-tight text-white drop-shadow">{doctor.firstName}<br/>{doctor.lastName}</h3>
            <p className="text-xs text-white/80 mt-1">{doctor.role}</p>
            <div className="mt-2 flex justify-end gap-2">
              <span className="chip bg-white/90 text-black border-transparent">{doctor.experience}</span>
              <span className="chip bg-white/90 text-black border-transparent">{doctor.rate}</span>
            </div>
          </div>
        </div>
        <div className="p-4 space-y-2.5">
          <Stat label="Heures travaillées" warn={doctor.hoursWorked.warn} ok={doctor.hoursWorked.ok} />
          <Stat label="Expérience" warn={doctor.exp.warn} ok={doctor.exp.ok} />
          <Stat label="Projets" warn={doctor.projects.warn} ok={doctor.projects.ok} />
          <div className="flex items-center justify-between rounded-xl border border-border bg-surface-2 px-4 py-3">
            <span className="text-sm">Période de travail</span>
            <button className="icon-btn h-7 w-7"><LayoutGrid size={12} /></button>
          </div>
        </div>
      </div>

      <div className="col-span-12 lg:col-span-7 grid gap-5">
        {show("identite") && (
          <div className="card-surface rounded-2xl p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BadgeCheck size={16} className="text-accent" />
                <h3 className="text-sm font-semibold">Identité professionnelle</h3>
              </div>
              <button onClick={onConfigure} className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 border border-border px-3 py-1.5 text-xs hover:border-accent/60">
                <Settings2 size={12} /> Configurer
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nom" value={doctor.lastName.toUpperCase()} />
              <Field label="Prénom" value={doctor.firstName} />
              <Field label="N° INPE" value={doctor.inpe} icon={<Hash size={12} />} />
              <Field label="N° d'autorisation" value={doctor.authNumber} />
              <Field label="Date d'inscription" value={doctor.joinDate} />
              <Field label="Statut" value={<span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-accent" />Actif</span>} />
            </div>
          </div>
        )}

        {show("specialites") && (
          <div className="card-surface rounded-2xl p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Stethoscope size={16} className="text-accent" />
                <h3 className="text-sm font-semibold">Spécialités &amp; exercice</h3>
              </div>
              <button onClick={onConfigure} className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 border border-border px-3 py-1.5 text-xs hover:border-accent/60">
                <Plus size={12} /> Modifier
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Spécialité principale" value={doctor.speciality} />
              <Field
                label="Exercice chirurgical"
                value={
                  <span className={`inline-flex items-center gap-1.5 ${doctor.surgical ? "text-accent" : "text-muted-foreground"}`}>
                    <Scissors size={12} /> {doctor.surgical ? "Oui" : "Non"}
                  </span>
                }
              />
            </div>
            <p className="mt-4 mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">Sous-spécialités</p>
            <div className="flex flex-wrap gap-1.5">
              {ALL_SPECIALITIES.map((s) => (
                <span key={s} className={`chip ${doctor.subSpecialities.includes(s) ? "border-accent/60 text-accent" : ""}`}>{s}</span>
              ))}
            </div>
          </div>
        )}

        {show("cabinet") && (
          <div className="card-surface rounded-2xl p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 size={16} className="text-accent" />
                <h3 className="text-sm font-semibold">Cabinet &amp; coordonnées</h3>
              </div>
              <button className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 border border-border px-3 py-1.5 text-xs hover:border-accent/60">
                <Edit3 size={12} /> Éditer
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Clinique / Hôpital" value={doctor.clinic} icon={<Building2 size={12} />} />
              <Field label="Ville" value={doctor.city} icon={<MapPin size={12} />} />
              <Field label="Adresse professionnelle" value={doctor.address} />
              <Field label="Téléphone" value={doctor.phone} icon={<Phone size={12} />} />
              <Field label="Email" value={doctor.email} icon={<Mail size={12} />} />
              <Field label="Faculté" value={doctor.faculty} icon={<GraduationCap size={12} />} />
            </div>
          </div>
        )}

        {(show("ngap") || show("pharmacie")) && (
          <div className={`grid grid-cols-1 gap-5 ${show("ngap") && show("pharmacie") ? "md:grid-cols-2" : ""}`}>
            {show("ngap") && (
              <div className="card-surface rounded-2xl p-5">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={16} className="text-accent" />
                    <h3 className="text-sm font-semibold">Cotation NGAP</h3>
                  </div>
                  <button onClick={onConfigure} className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 border border-border px-3 py-1.5 text-xs hover:border-accent/60">
                    <Plus size={12} /> Ajouter un acte
                  </button>
                </div>
                {doctor.ngap.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Aucun acte configuré.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {doctor.ngap.map((a) => (
                      <li key={a.code} className="flex items-center justify-between rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs">
                        <span><span className="font-mono text-accent">{a.code}</span> · {a.label}</span>
                        <span className="text-muted-foreground">{(a.base * a.coef).toFixed(2)} MAD</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {show("pharmacie") && (
              <div className="card-surface rounded-2xl p-5">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Pill size={16} className="text-accent" />
                    <h3 className="text-sm font-semibold">Pharmacie &amp; dispositifs</h3>
                  </div>
                  <button onClick={onConfigure} className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 border border-border px-3 py-1.5 text-xs hover:border-accent/60">
                    <Settings2 size={12} /> Gérer
                  </button>
                </div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Restrictions pharmacie</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {doctor.pharmacyRestrictions.length === 0
                    ? <span className="text-xs text-muted-foreground">Aucune</span>
                    : doctor.pharmacyRestrictions.map((r) => <span key={r} className="chip border-danger/40 text-danger">{r}</span>)}
                </div>
                <p className="mt-3 text-[10px] uppercase tracking-wider text-muted-foreground">Dispositifs médicaux</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {doctor.medicalDevices.length === 0
                    ? <span className="text-xs text-muted-foreground">Aucun</span>
                    : doctor.medicalDevices.map((r) => <span key={r} className="chip">{r}</span>)}
                </div>
              </div>
            )}
          </div>
        )}

        {show("personnel") && (
          <div className="card-surface rounded-2xl p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus size={16} className="text-accent" />
                <h3 className="text-sm font-semibold">Personnel paramédical &amp; accès</h3>
              </div>
              <button onClick={onConfigure} className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 border border-border px-3 py-1.5 text-xs hover:border-accent/60">
                <UserPlus size={12} /> Rattacher
              </button>
            </div>
            {doctor.staff.length === 0 ? (
              <p className="text-xs text-muted-foreground">Aucun personnel rattaché.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {doctor.staff.map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-xl border border-border bg-surface-2 px-3 py-2.5 text-xs">
                    <div>
                      <p className="font-semibold">{s.name}</p>
                      <p className="text-muted-foreground">{s.role} · {s.phone}</p>
                    </div>
                    <span className="chip border-accent/40 text-accent">{s.access}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {show("projets") && (
        <div className="col-span-12 card-surface rounded-2xl p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Temps projet</h3>
            <div className="flex gap-2">
              <button className="grid h-8 w-8 place-items-center rounded-full bg-accent text-accent-foreground"><MessageCircle size={14} /></button>
              <button className="icon-btn h-8 w-8"><LayoutGrid size={13} /></button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {PROJECTS.map((p) => (
              <div key={p.name} className="rounded-2xl border border-border bg-surface-2 p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">{p.icon} {p.name}</span>
                  <button className="icon-btn h-7 w-7"><ArrowUpRight size={12} /></button>
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  {p.warn && <span className="text-danger text-xs">!</span>}
                  <span className="text-2xl font-semibold">{p.hours}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{p.period}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, warn, ok }: { label: string; warn: number; ok: number }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-border bg-surface-2 px-4 py-3">
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-2">
        {warn > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-danger/15 text-danger px-2 py-0.5 text-xs ring-1 ring-danger/40">
            <AlertTriangle size={10} /> {warn}
          </span>
        )}
        <span className="inline-flex items-center gap-1 rounded-full bg-surface-3 px-2 py-0.5 text-xs">
          <CheckCircle2 size={10} className="text-accent" /> {ok}
        </span>
      </div>
    </div>
  );
}

function Field({ label, value, icon }: { label: string; value: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <span className="truncate">{value}</span>
      </div>
    </div>
  );
}

const NGAP_CATALOG: NgapAct[] = [
  { code: "C", label: "Consultation", base: 25, coef: 1 },
  { code: "CS", label: "Consultation spécialiste", base: 50, coef: 1 },
  { code: "CSC", label: "Consultation cardiologie", base: 52, coef: 1 },
  { code: "CSO", label: "Consultation ophtalmologie", base: 55, coef: 1 },
  { code: "C2", label: "Consultation psychiatrie", base: 55, coef: 1 },
  { code: "V", label: "Visite à domicile", base: 35, coef: 1 },
  { code: "VL", label: "Visite longue à domicile", base: 60, coef: 1 },
  { code: "K5", label: "Électrocardiogramme", base: 15, coef: 5 },
  { code: "K10", label: "Spirométrie", base: 15, coef: 10 },
];

const PHARMA_OPTIONS = [
  "Stupéfiants — sur ordonnance sécurisée",
  "Antibiotiques — usage hospitalier",
  "Immunosuppresseurs",
  "Médicaments d'exception",
];

const DEVICE_OPTIONS = [
  "Échographe",
  "ECG",
  "Spiromètre",
  "Générateur d'hémodialyse",
  "Bistouri électrique",
  "Microscope opératoire",
];

function DoctorWizard({
  doctor, onClose, onSave,
}: { doctor: Doctor; onClose: () => void; onSave: (d: Doctor) => void }) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Doctor>(doctor);
  const steps = ["Spécialités", "Pharmacie & dispositifs", "Cotation NGAP", "Personnel & accès"];
  const toggleArr = (arr: string[], v: string) =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-3xl bg-background border border-border shadow-[var(--shadow-glow)] flex flex-col"
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Configuration médecin</p>
            <h2 className="text-xl font-bold">{draft.firstName} {draft.lastName}</h2>
          </div>
          <button onClick={onClose} className="icon-btn h-9 w-9"><X size={15} /></button>
        </div>

        <div className="flex items-center gap-1 border-b border-border px-3 py-2 overflow-x-auto">
          {steps.map((s, i) => (
            <button
              key={s}
              onClick={() => setStep(i)}
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs whitespace-nowrap transition ${
                i === step ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-surface-2"
              }`}
            >
              <span className={`grid h-5 w-5 place-items-center rounded-full text-[10px] ${
                i === step ? "bg-accent-foreground/20" : "bg-surface-2"
              }`}>{i + 1}</span>
              {s}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto px-6 py-5 flex-1">
          {step === 0 && (
            <div className="space-y-5">
              <div>
                <p className="mb-2 text-xs font-semibold">Spécialité principale</p>
                <select
                  value={draft.speciality}
                  onChange={(e) => setDraft({ ...draft, speciality: e.target.value })}
                  className="w-full rounded-xl border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-accent"
                >
                  {ALL_SPECIALITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold">Sous-spécialités</p>
                <div className="flex flex-wrap gap-1.5">
                  {ALL_SPECIALITIES.map((s) => {
                    const on = draft.subSpecialities.includes(s);
                    return (
                      <button
                        key={s}
                        onClick={() => setDraft({ ...draft, subSpecialities: toggleArr(draft.subSpecialities, s) })}
                        className={`chip transition ${on ? "border-accent text-accent bg-accent/10" : "hover:border-foreground/40"}`}
                      >
                        {on && <Check size={11} className="mr-1 inline" />}{s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold">Exercice chirurgical ?</p>
                <div className="flex gap-2">
                  {[
                    { v: true, l: "Oui" },
                    { v: false, l: "Non" },
                  ].map((o) => (
                    <button
                      key={o.l}
                      onClick={() => setDraft({ ...draft, surgical: o.v })}
                      className={`flex-1 rounded-xl border px-4 py-3 text-sm transition ${
                        draft.surgical === o.v
                          ? "border-accent bg-accent/10 text-accent"
                          : "border-border bg-surface-2 hover:border-foreground/40"
                      }`}
                    >
                      <Scissors size={14} className="inline mr-2" /> {o.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <p className="mb-2 text-xs font-semibold flex items-center gap-2"><Pill size={13} /> Restrictions pharmacie</p>
                <div className="space-y-2">
                  {PHARMA_OPTIONS.map((p) => {
                    const on = draft.pharmacyRestrictions.includes(p);
                    return (
                      <label key={p} className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm cursor-pointer transition ${
                        on ? "border-accent bg-accent/10" : "border-border bg-surface-2 hover:border-foreground/40"
                      }`}>
                        <span>{p}</span>
                        <Toggle
                          on={on}
                          onChange={() => setDraft({ ...draft, pharmacyRestrictions: toggleArr(draft.pharmacyRestrictions, p) })}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-semibold flex items-center gap-2"><Syringe size={13} /> Dispositifs médicaux</p>
                <div className="space-y-2">
                  {DEVICE_OPTIONS.map((p) => {
                    const on = draft.medicalDevices.includes(p);
                    return (
                      <label key={p} className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm cursor-pointer transition ${
                        on ? "border-accent bg-accent/10" : "border-border bg-surface-2 hover:border-foreground/40"
                      }`}>
                        <span>{p}</span>
                        <Toggle
                          on={on}
                          onChange={() => setDraft({ ...draft, medicalDevices: toggleArr(draft.medicalDevices, p) })}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 2 && <NgapStep draft={draft} setDraft={setDraft} />}
          {step === 3 && <StaffStep draft={draft} setDraft={setDraft} />}
        </div>

        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <button
            onClick={() => setStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-xs disabled:opacity-40 hover:border-foreground/40"
          >
            <ChevronLeft size={13} /> Précédent
          </button>
          <span className="text-[11px] text-muted-foreground">Étape {step + 1} / {steps.length}</span>
          {step < steps.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              className="inline-flex items-center gap-1.5 rounded-full bg-foreground text-background px-4 py-2 text-xs hover:opacity-90"
            >
              Suivant <ChevronRight size={13} />
            </button>
          ) : (
            <button
              onClick={() => onSave(draft)}
              className="inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-4 py-2 text-xs hover:opacity-90"
            >
              <Check size={13} /> Enregistrer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`relative h-5 w-9 rounded-full transition ${on ? "bg-accent" : "bg-surface-3"}`}
    >
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-background transition ${on ? "left-[18px]" : "left-0.5"}`} />
    </button>
  );
}

function NgapStep({ draft, setDraft }: { draft: Doctor; setDraft: (d: Doctor) => void }) {
  const isSelected = (code: string) => draft.ngap.some((a) => a.code === code);
  const toggle = (a: NgapAct) =>
    setDraft({
      ...draft,
      ngap: isSelected(a.code) ? draft.ngap.filter((x) => x.code !== a.code) : [...draft.ngap, a],
    });
  const setCoef = (code: string, coef: number) =>
    setDraft({ ...draft, ngap: draft.ngap.map((a) => (a.code === code ? { ...a, coef } : a)) });

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold">Catalogue NGAP — {draft.ngap.length} acte(s) sélectionné(s)</p>
      </div>
      <div className="space-y-2">
        {NGAP_CATALOG.map((a) => {
          const on = isSelected(a.code);
          const current = draft.ngap.find((x) => x.code === a.code);
          return (
            <div
              key={a.code}
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition ${
                on ? "border-accent bg-accent/10" : "border-border bg-surface-2"
              }`}
            >
              <button
                onClick={() => toggle(a)}
                className={`grid h-6 w-6 place-items-center rounded-full border ${on ? "bg-accent border-accent text-accent-foreground" : "border-border"}`}
              >
                {on && <Check size={12} />}
              </button>
              <span className="grid h-7 min-w-[2.5rem] place-items-center rounded-md bg-surface-3 px-2 font-mono text-xs text-accent">{a.code}</span>
              <div className="flex-1">
                <p className="text-sm font-medium">{a.label}</p>
                <p className="text-[11px] text-muted-foreground">Base : {a.base.toFixed(2)} MAD</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-muted-foreground">Coef.</span>
                <input
                  type="number"
                  min={1}
                  disabled={!on}
                  value={current?.coef ?? a.coef}
                  onChange={(e) => setCoef(a.code, Math.max(1, Number(e.target.value) || 1))}
                  className="w-16 rounded-md border border-border bg-background px-2 py-1 text-sm outline-none focus:border-accent disabled:opacity-50"
                />
                <span className="w-24 text-right text-xs font-semibold text-accent">
                  {((current?.coef ?? a.coef) * a.base).toFixed(2)} MAD
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StaffStep({ draft, setDraft }: { draft: Doctor; setDraft: (d: Doctor) => void }) {
  const [form, setForm] = useState<Staff>({
    id: "", name: "", role: "Infirmier", phone: "", access: "Lecture",
  });

  const add = () => {
    if (!form.name.trim()) return;
    setDraft({ ...draft, staff: [...draft.staff, { ...form, id: crypto.randomUUID() }] });
    setForm({ id: "", name: "", role: "Infirmier", phone: "", access: "Lecture" });
  };
  const remove = (id: string) =>
    setDraft({ ...draft, staff: draft.staff.filter((s) => s.id !== id) });

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 text-xs font-semibold">Ajouter un membre</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <input
            placeholder="Nom complet"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-xl border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-accent"
          />
          <input
            placeholder="Téléphone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="rounded-xl border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-accent"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as Staff["role"] })}
            className="rounded-xl border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-accent"
          >
            <option>Infirmier</option>
            <option>Aide-soignant</option>
            <option>Secrétaire</option>
          </select>
          <select
            value={form.access}
            onChange={(e) => setForm({ ...form, access: e.target.value as Staff["access"] })}
            className="rounded-xl border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-accent"
          >
            <option>Lecture</option>
            <option>Lecture/Écriture</option>
            <option>Admin</option>
          </select>
        </div>
        <button
          onClick={add}
          className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-4 py-2 text-xs hover:opacity-90"
        >
          <Plus size={13} /> Ajouter
        </button>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold">Membres rattachés ({draft.staff.length})</p>
        {draft.staff.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
            Aucun infirmier / aide-soignant pour l'instant.
          </p>
        ) : (
          <ul className="space-y-2">
            {draft.staff.map((s) => (
              <li key={s.id} className="flex items-center justify-between rounded-xl border border-border bg-surface-2 px-4 py-3 text-sm">
                <div>
                  <p className="font-semibold">{s.name}</p>
                  <p className="text-xs text-muted-foreground">{s.role} · {s.phone || "—"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="chip border-accent/40 text-accent">{s.access}</span>
                  <button onClick={() => remove(s.id)} className="icon-btn h-8 w-8 text-danger">
                    <Trash2 size={13} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
